import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import {
  Layout,
  RedoCheckboxCard,
} from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox-card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { ChannelBadge, StatusBadge } from "../badges/conversation-badges";
import * as css from "./merge-ticket-modal.module.css";

export interface MergeTicketRowProps {
  conversation: ConversationListItemResponse;
  currentConversation: ConversationListItemResponse;
  isSelected: boolean;
  onToggle(id: ConversationListItemResponse["id"]): void;
}

export const MergeTicketRow = memo(function MergeTicketRow({
  conversation,
  currentConversation,
  isSelected,
  onToggle,
}: MergeTicketRowProps) {
  const isSameCustomer =
    conversation.mongoCustomerId === currentConversation.mongoCustomerId;

  const date = conversation.lastCustomerMessageAt ?? conversation.createdAt;
  const dateDisplay = date
    ? date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <RedoCheckboxCard
      checked={isSelected}
      inputSize="sm"
      inputType="checkbox"
      layout={Layout.SHORT}
      onClick={() => onToggle(conversation.id)}
      title={
        <Flex align="center" gap="xs" minW="none">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            {conversation.customer?.displayName ?? "Customer"}
          </Text>
          <Text fontSize="sm" textColor="tertiary">
            {`#${conversation.id.slice(0, 8)}`}
          </Text>
          {dateDisplay && (
            <Text fontSize="sm" textColor="tertiary">
              {dateDisplay}
            </Text>
          )}
        </Flex>
      }
    >
      <Flex dir="column" gap="md">
        {conversation.summary && (
          <Text
            fontSize="xs"
            overflow="hidden"
            textColor="tertiary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {conversation.summary}
          </Text>
        )}
        <Flex align="center" gap="xs">
          {isSameCustomer && (
            <RedoBadge
              color="orange"
              segmentLeading={{ type: "icon", Icon: AiStarIcon }}
              size="xs"
              text="Suggested"
            />
          )}
          <StatusBadge size="sm" status={conversation.status} />
          {conversation.channels.map((channel) => (
            <ChannelBadge channel={channel} key={channel} size="sm" />
          ))}
        </Flex>
      </Flex>
    </RedoCheckboxCard>
  );
});

export enum TicketRowPosition {
  First = "first",
  Middle = "middle",
  Last = "last",
}

const ticketRowPositionClass: Record<TicketRowPosition, string> = {
  [TicketRowPosition.First]: css.rowFirst as string,
  [TicketRowPosition.Middle]: css.rowMiddle as string,
  [TicketRowPosition.Last]: css.rowLast as string,
};

export const TicketRow = memo(function TicketRow({
  conversation,
  position = TicketRowPosition.Last,
  isSuggested = false,
}: {
  conversation: ConversationListItemResponse;
  position?: TicketRowPosition;
  isSuggested?: boolean;
}) {
  const date = conversation.lastCustomerMessageAt ?? conversation.createdAt;
  const dateDisplay = date
    ? date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <Flex
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      className={ticketRowPositionClass[position]}
      gap="sm"
      p="lg"
    >
      <Flex dir="column" gap="lg" minW="none">
        <Flex align="center" gap="xs">
          <Text fontSize="sm" fontWeight="medium" textColor="secondary">
            {conversation.customer?.displayName ?? "Customer"}
          </Text>
          <Text fontSize="sm" textColor="tertiary">
            {`#${conversation.id.slice(0, 8)}`}
          </Text>
          {dateDisplay && (
            <Text fontSize="sm" textColor="tertiary">
              {dateDisplay}
            </Text>
          )}
        </Flex>
        {conversation.summary && (
          <Text
            fontSize="xs"
            overflow="hidden"
            textColor="tertiary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {conversation.summary}
          </Text>
        )}
        <Flex align="center" gap="xs">
          {isSuggested && (
            <RedoBadge
              color="orange"
              segmentLeading={{ type: "icon", Icon: AiStarIcon }}
              size="sm"
              text="Suggested"
            />
          )}
          <StatusBadge size="sm" status={conversation.status} />
          {conversation.channels.map((channel) => (
            <ChannelBadge channel={channel} key={channel} size="sm" />
          ))}
        </Flex>
      </Flex>
    </Flex>
  );
});
